import { supabase } from "../../../foundation/altzero_base/base/utils/supabaseClient";

export interface LOA {
  id: string;
  org_id: string;
  client_id: string;
  file_id: string | null;
  signed_on: string | null;
  extn: Record<string, any>;
  created_at: string;
  // Joined data
  client?: {
    id: string;
    name: string;
  };
}

export interface LOAFormData {
  client_id: string;
  file_id?: string;
  signed_on?: string;
}

/**
 * Get user's organization ID
 */
const getUserOrganizationId = async (): Promise<string | null> => {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return null;

    const { data: orgMember, error } = await supabase
      .from("base_organisation_members")
      .select("organisation_id")
      .eq("user_id", user.id)
      .limit(1)
      .single();

    if (error) {
      console.error("Error getting org ID:", error);
      return null;
    }

    return orgMember?.organisation_id || null;
  } catch (error) {
    console.error("Error getting organization ID:", error);
    return null;
  }
};

/**
 * Get all LOAs for the user's organization
 */
export const getLOAs = async (): Promise<{
  success: boolean;
  data?: LOA[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("loa")
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .eq("org_id", orgId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error("Error fetching LOAs:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get LOAs by client ID
 */
export const getLOAsByClient = async (
  clientId: string
): Promise<{
  success: boolean;
  data?: LOA[];
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("loa")
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .eq("org_id", orgId)
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });

    if (error) throw error;

    return { success: true, data: data || [] };
  } catch (error: any) {
    console.error("Error fetching LOAs by client:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Create a new LOA
 */
export const createLOA = async (
  formData: LOAFormData
): Promise<{
  success: boolean;
  data?: LOA;
  error?: string;
}> => {
  try {
    const orgId = await getUserOrganizationId();
    if (!orgId) {
      return { success: false, error: "Organization not found" };
    }

    const { data, error } = await supabase
      .from("loa")
      .insert({
        org_id: orgId,
        client_id: formData.client_id,
        file_id: formData.file_id || null,
        signed_on: formData.signed_on || null,
        extn: {},
        created_at: new Date().toISOString(),
      })
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error creating LOA:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Update an existing LOA
 */
export const updateLOA = async (
  id: string,
  formData: Partial<LOAFormData>
): Promise<{
  success: boolean;
  data?: LOA;
  error?: string;
}> => {
  try {
    const updateData: any = {};

    if (formData.client_id !== undefined) updateData.client_id = formData.client_id;
    if (formData.file_id !== undefined) updateData.file_id = formData.file_id || null;
    if (formData.signed_on !== undefined) updateData.signed_on = formData.signed_on || null;

    const { data, error } = await supabase
      .from("loa")
      .update(updateData)
      .eq("id", id)
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error updating LOA:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Delete a LOA
 */
export const deleteLOA = async (
  id: string
): Promise<{
  success: boolean;
  error?: string;
}> => {
  try {
    const { error } = await supabase.from("loa").delete().eq("id", id);

    if (error) throw error;

    return { success: true };
  } catch (error: any) {
    console.error("Error deleting LOA:", error);
    return { success: false, error: error.message };
  }
};

/**
 * Get LOA by ID
 */
export const getLOAById = async (
  id: string
): Promise<{
  success: boolean;
  data?: LOA;
  error?: string;
}> => {
  try {
    const { data, error } = await supabase
      .from("loa")
      .select(`
        *,
        client:client_id (
          id,
          name
        )
      `)
      .eq("id", id)
      .single();

    if (error) throw error;

    return { success: true, data };
  } catch (error: any) {
    console.error("Error fetching LOA:", error);
    return { success: false, error: error.message };
  }
};

